import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { api } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Mail,
  Calendar,
  User,
  FileText,
  CheckCircle,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Wallet,
  Banknote,
  TrendingUp,
  AlertCircle,
  ArrowRight,
  ShieldCheck,
  Users,
  KeyRound,
  IndianRupee
} from 'lucide-react';
import { formatDate, formatCurrency } from '@/utils/helpers';
import { formatFullName } from '@/utils/nameFormatter';

function MemberDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [restrictDialogOpen, setRestrictDialogOpen] = useState(false);
  const [restrictReason, setRestrictReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [restricting, setRestricting] = useState(false);
  const [reset2FADialogOpen, setReset2FADialogOpen] = useState(false);
  const [resetting2FA, setResetting2FA] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txFilter, setTxFilter] = useState('all'); // all, emi, lavajam
  const [txDateRange, setTxDateRange] = useState('3m'); // 3m, 6m, 1y, 2y
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // UNIFIED COLLECTION: Payment dialog state for combined EMI + Lavajam collection
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    // Payment selection
    payEmi: true,
    payLavajam: true,
    // EMI details
    emiAmount: '',
    // Lavajam details
    lavajamAmount: '',
    // Common fields
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'cash',
    notes: '',
  });

  const [formData, setFormData] = useState({
    mandali_id: null,
    first_name: '',
    last_name: '',
    middle_name: '',
    nickname: '',
    contact_number: '',
    email: '',
    reference: '',
    opts_in_lavajam: false,
    opts_in_loan: false,
    opening_balance: 0,
  });

  useEffect(() => {
    fetchMemberDetails();
  }, [id]);

  // Fetch transactions when filters change
  useEffect(() => {
    if (id) {
      fetchTransactions();
    }
  }, [id, txFilter, txDateRange]);

  const fetchTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const txs = await api.accountHolders.getTransactions(id, txFilter, txDateRange);
      setTransactions(txs);
    } catch (err) {
      console.error("Failed to fetch transactions", err);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchMemberDetails = async () => {
    try {
      setLoading(true);
      const data = await api.accountHolders.getOne(id);
      setMember(data);

      // Populate form data for editing
      setFormData({
        mandali_id: data.mandali_id,
        first_name: data.first_name,
        last_name: data.last_name,
        middle_name: data.middle_name || '',
        nickname: data.nickname || '',
        contact_number: data.contact_number,
        email: data.email || '',
        reference: data.reference || '',
        opts_in_lavajam: data.opts_in_lavajam,
        opts_in_loan: data.opts_in_loan,
        opening_balance: data.opening_balance || 0,
      });
    } catch (error) {
      toast.error('Failed to load member details');
      navigate('/manager/members');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      // Ensure boolean fields are explicitly boolean (not null or undefined)
      // Use === true to convert any truthy/falsy to explicit true/false
      // Convert empty strings to null for optional fields to avoid Pydantic validation errors
      const cleanedData = {
        ...formData,
        opts_in_lavajam: formData.opts_in_lavajam === true,
        opts_in_loan: formData.opts_in_loan === true,
        opening_balance: Number(formData.opening_balance) || 0,
        middle_name: (formData.middle_name || '').trim() || null,
        nickname: (formData.nickname || '').trim() || null,
        email: (formData.email || '').trim() || null,
        reference: (formData.reference || '').trim() || null
      };

      await api.accountHolders.update(id, cleanedData);
      toast.success('Member updated successfully!');
      setEditDialogOpen(false);
      fetchMemberDetails();
    } catch (error) {
      console.error('Member update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update member');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteReason || deleteReason.trim().length < 5) {
      toast.error("Please provide a valid reason (min 5 chars)");
      return;
    }
    try {
      setDeleting(true);
      await api.accountHolders.delete(id, deleteReason);
      toast.success('Member deleted successfully!');
      navigate('/manager/members');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete member');
      setDeleting(false);
    }
  };

  const handleToggleRestriction = async () => {
    const isRestricting = !member.is_loan_restricted;

    if (isRestricting && (!restrictReason || restrictReason.trim().length < 5)) {
      toast.error("Please provide a valid reason (min 5 chars)");
      return;
    }

    try {
      setRestricting(true);
      await api.accountHolders.toggleRestriction(id, isRestricting, isRestricting ? restrictReason : null);
      toast.success(isRestricting ? 'Loan service restricted' : 'Loan service unrestricted');
      setRestrictDialogOpen(false);
      setRestrictReason('');
      fetchMemberDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update restriction');
    } finally {
      setRestricting(false);
    }
  };

  const handleReset2FA = async () => {
    setResetting2FA(true);
    try {
      // TODO: Implement API endpoint for 2FA reset
      // await api.accountHolders.reset2FA(id);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate delay
      toast.success('2FA has been reset for this user');
      setReset2FADialogOpen(false);
    } catch (error) {
      toast.error('Failed to reset 2FA');
    } finally {
      setResetting2FA(false);
    }
  };

  // UNIFIED COLLECTION: Handle combined EMI + Lavajam payment from member profile
  const handleUnifiedPayment = async (e) => {
    e.preventDefault();

    const hasEmi = member?.active_loan && paymentFormData.payEmi;
    const hasLavajam = member?.lavajam_account && paymentFormData.payLavajam;

    if (!hasEmi && !hasLavajam) {
      toast.error('Please select at least one payment type');
      return;
    }

    setProcessingPayment(true);
    const results = { emi: null, lavajam: null, errors: [] };

    try {
      // 1. Process EMI Payment
      if (hasEmi && parseFloat(paymentFormData.emiAmount) > 0) {
        try {
          const emiPaymentData = {
            loan_account_id: member.active_loan.id,
            installment_no: 0,
            amount_paid: parseFloat(paymentFormData.emiAmount),
            payment_date: paymentFormData.payment_date,
            payment_mode: paymentFormData.payment_mode,
            notes: paymentFormData.notes.trim() || null
          };

          const emiResponse = await api.loans.recordPayment(member.active_loan.id, emiPaymentData);
          results.emi = emiResponse;
        } catch (err) {
          results.errors.push(`EMI: ${err.response?.data?.detail || 'Failed'}`);
        }
      }

      // 2. Process Lavajam Payment
      if (hasLavajam && parseFloat(paymentFormData.lavajamAmount) > 0) {
        try {
          // Get current month in YYYY-MM format
          const currentMonth = new Date().toISOString().slice(0, 7);

          const lavajamPaymentData = {
            month: currentMonth,
            amount_paid: parseFloat(paymentFormData.lavajamAmount),
            payment_date: paymentFormData.payment_date,
            payment_mode: paymentFormData.payment_mode,
            notes: paymentFormData.notes.trim() || null
          };

          const lavajamResponse = await api.lavajam.recordPayment(member.lavajam_account.id, lavajamPaymentData);
          results.lavajam = lavajamResponse;
        } catch (err) {
          results.errors.push(`Lavajam: ${err.response?.data?.detail || 'Failed'}`);
        }
      }

      // 3. Show appropriate success/error messages
      if (results.errors.length === 0) {
        let successMsg = '✅ Payment recorded successfully!';
        const parts = [];
        if (results.emi) parts.push(`EMI ₹${paymentFormData.emiAmount}`);
        if (results.lavajam) parts.push(`Lavajam ₹${paymentFormData.lavajamAmount}`);
        if (parts.length > 0) successMsg += ` (${parts.join(' + ')})`;
        toast.success(successMsg);
        setPaymentDialogOpen(false);
      } else if (results.emi || results.lavajam) {
        // Partial success
        toast.warning(`Partial success: ${results.errors.join(', ')}`);
        setPaymentDialogOpen(false);
      } else {
        // All failed
        toast.error(`Payment failed: ${results.errors.join(', ')}`);
      }

      // Reset form and refresh member data
      setPaymentFormData({
        payEmi: true,
        payLavajam: true,
        emiAmount: '',
        lavajamAmount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_mode: 'cash',
        notes: '',
      });
      fetchMemberDetails();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Helper: Open payment dialog with pre-filled amounts
  const openUnifiedPaymentDialog = () => {
    const emiAmount = member?.active_loan?.emi_amount || 0;
    const lavajamAmount = member?.lavajam_account?.monthly_amount || 0;

    setPaymentFormData({
      payEmi: !!member?.active_loan,
      payLavajam: !!member?.lavajam_account,
      emiAmount: emiAmount > 0 ? emiAmount.toString() : '',
      lavajamAmount: lavajamAmount > 0 ? lavajamAmount.toString() : '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: 'cash',
      notes: '',
    });
    setPaymentDialogOpen(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6 fade-in pt-2">
          <div className="skeleton h-12 w-64"></div>
          <Card className="skeleton h-96"></Card>
        </div>
      </DashboardLayout>
    );
  }

  if (!member) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-96 space-y-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800">Member Not Found</h2>
            <p className="text-gray-600 mt-2">The requested member profile could not be loaded.</p>
          </div>
          <Button onClick={() => navigate('/manager/members')} variant="default">
            Return to Members List
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in pt-2">
        {/* Header - Consistent spacing */}
        <div className="flex items-center justify-between flex-wrap gap-4 pb-2">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate('/manager/members')}
              className="hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold gradient-text">
                Member Profile
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                View and manage member information
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              variant={member.is_loan_restricted ? "outline" : "secondary"}
              className={`w-full sm:w-auto ${member.is_loan_restricted ? "border-red-200 text-red-600 hover:bg-red-50" : "bg-orange-100 text-orange-700 hover:bg-orange-200"}`}
              onClick={() => setRestrictDialogOpen(true)}
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              {member.is_loan_restricted ? "Unrestrict Loan" : "Restrict Loan"}
            </Button>
            <Button
              onClick={() => setEditDialogOpen(true)}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
            <Button
              variant="destructive"
              className="w-full sm:w-auto"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info Card */}
          <Card className="lg:col-span-2 glass border-0 shadow-soft">
            <CardHeader>
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg text-white font-bold text-xl md:text-3xl shrink-0">
                  {member.first_name?.charAt(0) || ''}{member.last_name?.charAt(0) || ''}
                </div>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-xl md:text-2xl font-bold text-gray-800">
                    {formatFullName(member.first_name, member.middle_name, member.last_name)}
                  </CardTitle>
                  {member.nickname && (
                    <Badge variant="secondary" className="mt-2">
                      "{member.nickname}"
                    </Badge>
                  )}
                  <p className="text-emerald-600 font-semibold mt-2 text-lg">
                    {member.account_number}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Financial Status Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Card 1: Loan Status */}
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100 shadow-sm">
                  <CardContent className="p-4">
                    {/* OVERDUE ALERT BANNER */}
                    {member.active_loan && (() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const endDate = new Date(member.active_loan.end_date);
                      endDate.setHours(0, 0, 0, 0);
                      const isOverdue = endDate < today && (member.active_loan.outstanding_principal || 0) > 0;

                      if (!isOverdue) return null;

                      const daysOverdue = Math.floor((today - endDate) / (1000 * 60 * 60 * 24));
                      const monthsOverdue = Math.floor(daysOverdue / 30);

                      return (
                        <div className="mb-3 p-3 bg-red-100 border border-red-300 rounded-lg">
                          <div className="flex items-center gap-2 text-red-800">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="font-bold text-sm">OVERDUE BY {monthsOverdue} MONTH{monthsOverdue !== 1 ? 'S' : ''}</span>
                          </div>
                          <p className="text-xs text-red-700 mt-1">Last Due: {formatDate(member.active_loan.end_date)}</p>
                        </div>
                      );
                    })()}

                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                        <Banknote className="w-4 h-4" /> Loan Status
                      </h4>
                      {member.active_loan ? (
                        <Badge className="bg-blue-200 text-blue-800 hover:bg-blue-200 border-blue-300">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-500 bg-white/50">Not Availed</Badge>
                      )}
                    </div>
                    {member.active_loan ? (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-blue-700">Loan Account:</span>
                          <span className="font-medium text-blue-900">{member.active_loan.loan_account_number}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-blue-700">Principal:</span>
                          <span className="font-medium text-blue-900">{formatCurrency(member.active_loan.principal_amount)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-blue-700">Outstanding:</span>
                          <span className="font-bold text-red-600">{formatCurrency(member.active_loan.outstanding_principal)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-blue-700">EMI Amount:</span>
                          <span className="font-bold text-blue-900">{formatCurrency(member.active_loan.emi_amount)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-blue-600 mt-2 pt-2 border-t border-blue-200/60">
                          <span>Ends: {formatDate(member.active_loan.end_date)}</span>
                          <span>{member.active_loan.outstanding_installments} EMIs left</span>
                        </div>

                        {/* VIEW DETAILS BUTTON - Payment moved to unified collector */}
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full mt-3 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                          onClick={() => navigate(`/manager/loans/${member.active_loan.id}`)}
                        >
                          View Loan Details
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-500 text-sm bg-white/30 rounded-lg">
                        No active loans
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Card 2: Lavajam Status */}
                <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-emerald-900 flex items-center gap-2">
                        <Wallet className="w-4 h-4" /> Lavajam Status
                      </h4>
                      {member.lavajam_account ? (
                        <Badge className="bg-emerald-200 text-emerald-800 hover:bg-emerald-200 border-emerald-300">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-500 bg-white/50">Not Availed</Badge>
                      )}
                    </div>
                    {member.lavajam_account ? (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-emerald-700">Monthly Deposit:</span>
                          <span className="font-medium text-emerald-900">{formatCurrency(member.lavajam_account.monthly_amount)}</span>
                        </div>
                        <div className="mt-4 pt-3 border-t border-emerald-200/60">
                          <p className="text-xs text-emerald-600 mb-1">Total Saved</p>
                          <p className="font-bold text-emerald-800 text-2xl">{formatCurrency(member.lavajam_account.current_balance)}</p>
                        </div>
                        {/* VIEW DETAILS BUTTON - Payment moved to unified collector */}
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full mt-3 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => navigate(`/manager/lavajam/${member.lavajam_account.id}`)}
                        >
                          View Lavajam Details
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-500 text-sm bg-white/30 rounded-lg">
                        No active lavajam
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Card 3: Total Monthly Obligation + UNIFIED COLLECT PAYMENT */}
                <Card className="bg-gradient-to-br from-purple-50 to-fuchsia-50 border-purple-100 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-purple-900 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" /> Total Monthly Obligation
                      </h4>
                      <Badge className="bg-purple-200 text-purple-800 hover:bg-purple-200 border-purple-300">Due</Badge>
                    </div>
                    <div className="flex flex-col items-center justify-center py-3">
                      <span className="text-2xl md:text-4xl font-bold text-purple-700 tracking-tight">
                        {formatCurrency(member.total_monthly_collection)}
                      </span>
                      <span className="text-xs font-medium text-purple-600 mt-1 uppercase tracking-wide">Total Monthly Due</span>
                    </div>
                    {member.total_monthly_collection > 0 && (
                      <div className="mt-3 pt-3 border-t border-purple-200/60 space-y-2">
                        <div className="text-xs">
                          <p className="font-semibold text-purple-800 mb-2">Breakdown:</p>
                          {member.active_loan && (
                            <div className="flex justify-between text-purple-700">
                              <span>• Loan EMI:</span>
                              <span className="font-medium">{formatCurrency(member.active_loan?.emi_amount || 0)} (Due: 20th)</span>
                            </div>
                          )}
                          {member.lavajam_account && (
                            <div className="flex justify-between text-purple-700 mt-1">
                              <span>• Lavajam:</span>
                              <span className="font-medium">{formatCurrency(member.lavajam_account?.monthly_amount || 0)} (Due: 20th)</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* UNIFIED COLLECT PAYMENT BUTTON */}
                    {(member.active_loan || member.lavajam_account) && (
                      <Button
                        size="lg"
                        className="w-full mt-4 bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white shadow-lg font-bold"
                        onClick={openUnifiedPaymentDialog}
                      >
                        <IndianRupee className="w-5 h-5 mr-2" />
                        💰 Collect Payment
                      </Button>
                    )}
                  </CardContent>
                </Card>

                {/* Card 4: Profit Share (from Interest Distribution) */}
                <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-amber-900 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" /> Profit Share
                      </h4>
                      <Badge className="bg-amber-200 text-amber-800 hover:bg-amber-200 border-amber-300">Accumulated</Badge>
                    </div>
                    <div className="flex flex-col items-center justify-center py-3">
                      <span className="text-2xl md:text-4xl font-bold text-amber-700 tracking-tight">
                        {formatCurrency(member.profit_share_balance || 0)}
                      </span>
                      <span className="text-xs font-medium text-amber-600 mt-1 uppercase tracking-wide">Total Earned</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-amber-200/60">
                      <p className="text-xs text-amber-700 text-center">
                        Your share from monthly interest distributions
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Personal Information */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  Personal Information
                </h3>
                <Separator className="mb-4" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">First Name</p>
                    <p className="font-medium text-gray-800">{member.first_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Middle Name</p>
                    <p className="font-medium text-gray-800">{member.middle_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Last Name</p>
                    <p className="font-medium text-gray-800">{member.last_name}</p>
                  </div>
                  {member.nickname && (
                    <div className="col-span-3">
                      <p className="text-sm text-gray-500">Nickname</p>
                      <p className="font-medium text-gray-800">{member.nickname}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Guarantor Information (Only if Active Loan exists AND guarantor data exists) */}
              {member.active_loan && (member.active_loan.guarantor1_name || member.active_loan.guarantor2_name) && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-indigo-600" />
                    Guarantor Information
                  </h3>
                  <Separator className="mb-4" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {member.active_loan.guarantor1_name && (
                      <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                        <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-2">Primary Guarantor</p>
                        <div className="flex items-center gap-3 mb-1">
                          <User className="w-4 h-4 text-gray-500" />
                          <p className="font-medium text-gray-800">{member.active_loan.guarantor1_name}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-gray-500" />
                          <p className="text-sm text-gray-600">{member.active_loan.guarantor1_phone || 'No phone'}</p>
                        </div>
                      </div>
                    )}
                    {member.active_loan.guarantor2_name && (
                      <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                        <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-2">Secondary Guarantor</p>
                        <div className="flex items-center gap-3 mb-1">
                          <User className="w-4 h-4 text-gray-500" />
                          <p className="font-medium text-gray-800">{member.active_loan.guarantor2_name}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-gray-500" />
                          <p className="text-sm text-gray-600">{member.active_loan.guarantor2_phone || 'No phone'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-green-600" />
                  Contact Information
                </h3>
                <Separator className="mb-4" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Phone Number</p>
                      <p className="font-medium text-gray-800">{member.contact_number}</p>
                    </div>
                  </div>
                  {member.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium text-gray-800">{member.email}</p>
                      </div>
                    </div>
                  )}

                </div>
              </div>

              {/* Reference Details */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  Reference Details
                </h3>
                <Separator className="mb-4" />
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Reference Name / Contact</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="font-medium text-gray-800 text-lg">
                        {member.reference || 'No reference provided'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Join Date</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <p className="font-medium text-gray-800">{formatDate(member.join_date)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transaction History */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-orange-600" />
                    Transaction History
                  </h3>

                  {/* Filters */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                    {/* Type Filter */}
                    <Select value={txFilter} onValueChange={setTxFilter}>
                      <SelectTrigger className="w-full sm:w-[130px] h-9">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="emi">EMI Only</SelectItem>
                        <SelectItem value="lavajam">Lavajam Only</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Date Range Filter */}
                    <Select value={txDateRange} onValueChange={setTxDateRange}>
                      <SelectTrigger className="w-full sm:w-[140px] h-9">
                        <SelectValue placeholder="Date Range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3m">Last 3 Months</SelectItem>
                        <SelectItem value="6m">Last 6 Months</SelectItem>
                        <SelectItem value="1y">Last 1 Year</SelectItem>
                        <SelectItem value="2y">Last 2 Years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Separator className="mb-4" />

                {loadingTransactions ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                    <span className="ml-2 text-gray-500">Loading transactions...</span>
                  </div>
                ) : transactions.length > 0 ? (
                  <>
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                          <tr>
                            <th className="px-4 py-3">Date</th>
                            <th className="px-4 py-3">Type</th>
                            <th className="px-4 py-3">Description</th>
                            <th className="px-4 py-3 text-right">Amount</th>
                            <th className="px-4 py-3 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((tx) => (
                            <tr key={tx.id} className="bg-white border-b hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                {formatDate(tx.date)}
                              </td>
                              <td className="px-4 py-3">
                                <Badge
                                  className={tx.type === 'Lavajam'
                                    ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'
                                    : 'bg-blue-100 text-blue-800 hover:bg-blue-100'}>
                                  {tx.type}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-gray-500">
                                {tx.description}
                                {tx.reference_id && <span className="block text-xs text-gray-400">Ref: {tx.reference_id}</span>}
                              </td>
                              <td className="px-4 py-3 text-right font-medium">
                                {formatCurrency(tx.amount)}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                                  {tx.status}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 border-t">
                        Showing {transactions.length} transaction{transactions.length !== 1 ? 's' : ''} from {txDateRange === '3m' ? 'last 3 months' : txDateRange === '6m' ? 'last 6 months' : txDateRange === '1y' ? 'last 1 year' : 'last 2 years'}
                      </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-3">
                      {transactions.map((tx) => (
                        <Card key={tx.id} className="p-4 hover:shadow-md transition-shadow">
                          {/* Date & Type */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-sm font-semibold text-gray-900">
                              {formatDate(tx.date)}
                            </div>
                            <Badge
                              className={tx.type === 'Lavajam'
                                ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'
                                : 'bg-blue-100 text-blue-800 hover:bg-blue-100'}>
                              {tx.type}
                            </Badge>
                          </div>

                          {/* Description */}
                          <div className="mb-3 pb-3 border-b">
                            <div className="text-sm text-gray-600">
                              {tx.description}
                            </div>
                            {tx.reference_id && (
                              <div className="text-xs text-gray-400 mt-1">
                                Ref: {tx.reference_id}
                              </div>
                            )}
                          </div>

                          {/* Amount & Status */}
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xs text-gray-500 mb-1">Amount</div>
                              <div className="text-lg font-bold text-gray-900">
                                {formatCurrency(tx.amount)}
                              </div>
                            </div>
                            <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                              {tx.status}
                            </Badge>
                          </div>
                        </Card>
                      ))}
                      <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 rounded-lg text-center">
                        Showing {transactions.length} transaction{transactions.length !== 1 ? 's' : ''} from {txDateRange === '3m' ? 'last 3 months' : txDateRange === '6m' ? 'last 6 months' : txDateRange === '1y' ? 'last 1 year' : 'last 2 years'}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                    <p>No transactions found</p>
                    <p className="text-xs mt-1">Try adjusting the filters or date range</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Side Info Cards */}
          <div className="space-y-6">
            {/* Account Status */}
            <Card className="glass border-0 shadow-soft">
              <CardHeader>
                <CardTitle className="text-lg">Account Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    {member.opts_in_loan ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="text-sm font-medium">Loan Services</span>
                  </div>
                  {member.opts_in_loan ? (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Not Availed</Badge>
                  )}
                </div>

                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    {member.opts_in_lavajam ? (
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="text-sm font-medium">Lavajam Services</span>
                  </div>
                  {member.opts_in_lavajam ? (
                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Not Availed</Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="glass border-0 shadow-soft">
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start" onClick={() => setEditDialogOpen(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Member
                </Button>
                <Button variant="outline" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteDialogOpen(true)}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Member
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl gradient-text">Edit Member Details</DialogTitle>
              <DialogDescription>
                Update member information
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleUpdate} className="space-y-6 mt-4">
              {/* Personal Details */}
              <div className="space-y-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <User className="w-5 h-5 text-emerald-600" />
                  Personal Details
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_first_name">First Name *</Label>
                    <Input
                      id="edit_first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit_middle_name">Middle Name</Label>
                    <Input
                      id="edit_middle_name"
                      value={formData.middle_name}
                      onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit_last_name">Last Name *</Label>
                    <Input
                      id="edit_last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_nickname">Nickname</Label>
                  <Input
                    id="edit_nickname"
                    value={formData.nickname}
                    onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                  />
                </div>
              </div>

              {/* Contact Details */}
              <div className="space-y-4 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-blue-600" />
                  Contact Information
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_contact_number">Contact Number *</Label>
                    <Input
                      id="edit_contact_number"
                      value={formData.contact_number}
                      onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                      required
                      pattern="[0-9]{10}"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit_email">Email</Label>
                    <Input
                      id="edit_email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_reference">Reference</Label>
                  <Input
                    id="edit_reference"
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  />
                </div>
              </div>

              {/* Account Options */}
              <div className="space-y-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                <h3 className="font-semibold text-gray-800">Account Options</h3>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="edit_opts_in_lavajam"
                      checked={formData.opts_in_lavajam}
                      onCheckedChange={(checked) => setFormData({ ...formData, opts_in_lavajam: checked === true })}
                    />
                    <Label htmlFor="edit_opts_in_lavajam" className="cursor-pointer">
                      Enable Lavajam Services
                    </Label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="edit_opts_in_loan"
                      checked={formData.opts_in_loan}
                      onCheckedChange={(checked) => setFormData({ ...formData, opts_in_loan: checked === true })}
                    />
                    <Label htmlFor="edit_opts_in_loan" className="cursor-pointer">
                      Enable Loan Services
                    </Label>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={updating}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                >
                  {updating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Member'
                  )}
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} disabled={updating} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="w-[95vw] max-w-md sm:w-full max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <DialogTitle className="text-xl">Delete Member?</DialogTitle>
                  <DialogDescription className="mt-1">
                    This action cannot be undone
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="py-4">
              <p className="text-gray-700">
                Are you sure you want to delete <span className="font-semibold">{formatFullName(member.first_name, member.middle_name, member.last_name)}</span>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Account Number: {member.account_number}
              </p>
              <div className="mt-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="delete-reason">Reason for Deletion <span className="text-red-500">*</span></Label>
                  <Input
                    id="delete-reason"
                    placeholder="Enter reason (mandatory)"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">Minimum 5 characters required.</p>
                </div>
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> Members with active loans cannot be deleted.
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Member'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Restrict Dialog */}
        <Dialog open={restrictDialogOpen} onOpenChange={setRestrictDialogOpen}>
          <DialogContent className="w-[95vw] max-w-md sm:w-full max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <DialogTitle className="text-xl">
                    {member.is_loan_restricted ? "Unrestrict Loan Service?" : "Restrict Loan Service?"}
                  </DialogTitle>
                  <DialogDescription className="mt-1">
                    {member.is_loan_restricted
                      ? "This will allow the member to apply for loans again."
                      : "This will prevent the member from applying for new loans."}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="py-4">
              {!member.is_loan_restricted && (
                <div className="space-y-2">
                  <Label htmlFor="restrict-reason">Reason for Restriction <span className="text-red-500">*</span></Label>
                  <Input
                    id="restrict-reason"
                    placeholder="Enter reason (mandatory)"
                    value={restrictReason}
                    onChange={(e) => setRestrictReason(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">Minimum 5 characters required.</p>
                </div>
              )}
              {member.is_loan_restricted && (
                <p className="text-gray-700">
                  Are you sure you want to remove the restriction?
                </p>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setRestrictDialogOpen(false)} disabled={restricting}>
                Cancel
              </Button>
              <Button
                variant={member.is_loan_restricted ? "default" : "destructive"}
                onClick={handleToggleRestriction}
                disabled={restricting}
              >
                {restricting ? 'Updating...' : (member.is_loan_restricted ? 'Unrestrict' : 'Restrict')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset 2FA Dialog */}
        <Dialog open={reset2FADialogOpen} onOpenChange={setReset2FADialogOpen}>
          <DialogContent className="w-[95vw] max-w-md sm:w-full max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <KeyRound className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <DialogTitle className="text-xl">Reset 2FA?</DialogTitle>
                  <DialogDescription className="mt-1">
                    This will disable 2FA for this user.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="py-4">
              <p className="text-gray-700">
                Are you sure you want to reset 2FA for <strong>{formatFullName(member.first_name, member.middle_name, member.last_name)}</strong>?
              </p>
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> The user will be required to set up 2FA again on their next login.
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setReset2FADialogOpen(false)} disabled={resetting2FA}>
                Cancel
              </Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={handleReset2FA}
                disabled={resetting2FA}
              >
                {resetting2FA ? 'Resetting...' : 'Confirm Reset'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* UNIFIED COLLECTION DIALOG - Record EMI + Lavajam payment together */}
        {(member?.active_loan || member?.lavajam_account) && (
          <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
            <DialogContent className="w-[95vw] max-w-lg sm:w-full max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl gradient-text flex items-center gap-2">
                  <IndianRupee className="w-5 h-5" />
                  💰 Collect Payment
                </DialogTitle>
                <DialogDescription>
                  {formatFullName(member.first_name, member.middle_name, member.last_name)} ({member.account_number})
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleUnifiedPayment} className="space-y-4">
                {/* Payment Type Selection */}
                <div className="space-y-3">
                  {/* EMI Selection */}
                  {member.active_loan && (
                    <label
                      className={`block p-4 rounded-lg border-2 transition-all cursor-pointer ${paymentFormData.payEmi
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-gray-50 opacity-60'
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={paymentFormData.payEmi}
                            onChange={(e) => setPaymentFormData(prev => ({ ...prev, payEmi: e.target.checked }))}
                            className="w-5 h-5 rounded border-2 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <p className="font-semibold text-blue-900 flex items-center gap-2">
                              <Banknote className="w-4 h-4" /> Loan EMI
                            </p>
                            <p className="text-xs text-gray-600">{member.active_loan.loan_account_number}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-blue-700 text-lg">
                            {formatCurrency(member.active_loan.emi_amount)}
                          </p>
                          <p className="text-xs text-gray-500">{member.active_loan.outstanding_installments} EMIs left</p>
                        </div>
                      </div>
                      {paymentFormData.payEmi && (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <Label className="text-xs text-blue-700">EMI Amount (₹)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={paymentFormData.emiAmount}
                            onChange={(e) => setPaymentFormData(prev => ({ ...prev, emiAmount: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                            placeholder={member.active_loan.emi_amount?.toString()}
                            className="mt-1 bg-white"
                          />
                        </div>
                      )}
                    </label>
                  )}

                  {/* Lavajam Selection */}
                  {member.lavajam_account && (
                    <label
                      className={`block p-4 rounded-lg border-2 transition-all cursor-pointer ${paymentFormData.payLavajam
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 bg-gray-50 opacity-60'
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={paymentFormData.payLavajam}
                            onChange={(e) => setPaymentFormData(prev => ({ ...prev, payLavajam: e.target.checked }))}
                            className="w-5 h-5 rounded border-2 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div>
                            <p className="font-semibold text-emerald-900 flex items-center gap-2">
                              <Wallet className="w-4 h-4" /> Lavajam
                            </p>
                            <p className="text-xs text-gray-600">Monthly Savings</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-700 text-lg">
                            {formatCurrency(member.lavajam_account.monthly_amount)}
                          </p>
                          <p className="text-xs text-gray-500">Balance: {formatCurrency(member.lavajam_account.current_balance)}</p>
                        </div>
                      </div>
                      {paymentFormData.payLavajam && (
                        <div className="mt-3 pt-3 border-t border-emerald-200">
                          <Label className="text-xs text-emerald-700">Lavajam Amount (₹)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={paymentFormData.lavajamAmount}
                            onChange={(e) => setPaymentFormData(prev => ({ ...prev, lavajamAmount: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                            placeholder={member.lavajam_account.monthly_amount?.toString()}
                            className="mt-1 bg-white"
                          />
                        </div>
                      )}
                    </label>
                  )}
                </div>

                {/* Total Summary */}
                <div className="p-4 bg-gradient-to-r from-purple-100 to-fuchsia-100 rounded-lg border border-purple-200">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-purple-800">Total Amount:</span>
                    <span className="text-2xl font-bold text-purple-700">
                      {formatCurrency(
                        (paymentFormData.payEmi ? parseFloat(paymentFormData.emiAmount) || 0 : 0) +
                        (paymentFormData.payLavajam ? parseFloat(paymentFormData.lavajamAmount) || 0 : 0)
                      )}
                    </span>
                  </div>
                  <div className="text-xs text-purple-600 mt-1">
                    {paymentFormData.payEmi && paymentFormData.emiAmount && `EMI: ₹${paymentFormData.emiAmount}`}
                    {paymentFormData.payEmi && paymentFormData.payLavajam && paymentFormData.emiAmount && paymentFormData.lavajamAmount && ' + '}
                    {paymentFormData.payLavajam && paymentFormData.lavajamAmount && `Lavajam: ₹${paymentFormData.lavajamAmount}`}
                  </div>
                </div>

                {/* Common Payment Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="unified_pay_date">Payment Date *</Label>
                    <Input
                      id="unified_pay_date"
                      type="date"
                      value={paymentFormData.payment_date}
                      max={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setPaymentFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="unified_pay_mode">Payment Mode</Label>
                    <div className="h-10 px-3 py-2 bg-gray-100 rounded-md border border-gray-200 text-gray-700 flex items-center font-medium">
                      💵 Cash
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="unified_pay_notes">Notes (Optional)</Label>
                  <Input
                    id="unified_pay_notes"
                    value={paymentFormData.notes}
                    onChange={(e) => setPaymentFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional notes"
                  />
                </div>

                <DialogFooter className="gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPaymentDialogOpen(false)}
                    disabled={processingPayment}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700"
                    disabled={processingPayment || (!paymentFormData.payEmi && !paymentFormData.payLavajam)}
                  >
                    {processingPayment ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Record Payment
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </DashboardLayout>
  );
}

export default MemberDetails;
